import axios from 'axios'
import api from '@/lib/api/axios'

/**
 * The customer knowledge base (backend/src/routes/api/knowledge.js): facts about this estate,
 * confirmed by its own people, that the AI agents use in their answers and runbook generation uses
 * for the objects in a runbook. Kept in English; the AI Assistant answers in the person's language.
 */

/** pending: saved from a thumbs up, waiting for someone else to approve it. Only active is used. */
export type KnowledgeStatus = 'pending' | 'active' | 'paused' | 'retired' | 'rejected'

export interface KnowledgeEntry {
  EntryID: number
  Text: string
  AgentID: string
  AgentName: string
  Scope: 'object' | 'technology'
  ObjectDbID: number | null
  ObjectName: string | null
  ObjectGone: number
  TechnologyID: number | null
  TechnologyName: string | null
  TechnologyGone: number
  /** How many objects in the estate the technology knowledge reaches today. */
  TechnologyObjects: number | null
  Status: KnowledgeStatus
  Source: 'answer' | 'manual' | 'report'
  SourceQuestion: string | null
  ConfirmedBy: number
  ConfirmedByName: string | null
  ConfirmedAt: string
  ApprovedByName: string | null
  ApprovedAt: string | null
  /** Why it was rejected. */
  DecisionNote: string | null
  UpdatedByName: string | null
  UpdatedAt: string | null
  Uses: number
  AnswerUses: number
  RunbookUses: number
  LastUsedAt: string | null
  OpenReports: number
  Unused30Days: number
}

export interface KnowledgeReport {
  ReportID: number
  AgentID: string | null
  AgentName: string | null
  Question: string | null
  Answer: string | null
  WhatWrong: string
  WhatRight: string | null
  Status: 'open' | 'corrected' | 'dismissed'
  ReportedByName: string | null
  ReportedAt: string
  ClosedByName: string | null
  ClosedAt: string | null
  OutcomeNote: string | null
  CorrectedEntryID: number | null
  PausedEntries: number
}

export interface KnowledgeObject { id: number; name: string; technologyId: number | null; technologyName: string | null }
export interface KnowledgeOptions {
  agents: { id: string; label: string }[]
  objects: KnowledgeObject[]
  technologies: { id: number; name: string; objects: number }[]
}

/** What a fact applies to: exactly one of the two. */
export interface Scope { objectId: number | null; technologyId: number | null }

export interface ProposedFact extends Scope { text: string }
export interface Proposal {
  agentId: string | null
  agentName: string
  objects: KnowledgeObject[]
  facts: ProposedFact[]
  leftOut: { text: string; reason: 'general' | 'estate_data' | 'secret' | 'not_specific' }[]
}

export interface UsedKnowledge { id: number; text: string; confirmedByName: string | null }

export interface EntryFilters { agent?: string; objectId?: number; status?: string; needsReview?: boolean; q?: string }

/**
 * A save the server refused because the text was not in English: the English it would save, in the
 * same order as what was sent, for the person to check and save again.
 */
export class NotEnglish extends Error {
  constructor(public english: string[]) { super('not_english') }
}

/** The server's own message for a refused request, or null. */
export function refusal(error: unknown): string | null {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { message?: string } | undefined
    return body?.message ?? null
  }
  return null
}

async function saving<T>(request: Promise<T>): Promise<T> {
  try {
    return await request
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      const body = error.response.data as { error?: string; data?: { english?: string[] } }
      if (body?.error === 'not_english' && body.data?.english) throw new NotEnglish(body.data.english)
    }
    throw error
  }
}

export const knowledgeApi = {
  entries: (f: EntryFilters = {}) =>
    api.get<{ data: { entries: KnowledgeEntry[] } }>('/api/knowledge/entries', {
      params: {
        agent: f.agent || undefined,
        objectId: f.objectId ?? undefined,
        status: f.status || undefined,
        needsReview: f.needsReview ? '1' : undefined,
        q: f.q || undefined,
      },
    }).then((r) => r.data.data.entries),

  options: () => api.get<{ data: KnowledgeOptions }>('/api/knowledge/options').then((r) => r.data.data),

  add: (body: { text: string; agentId: string; reportId?: number; retirePaused?: boolean } & Scope) =>
    saving(api.post<{ data: { id: number } }>('/api/knowledge/entries', body).then((r) => r.data.data)),

  approve: (id: number) => api.post<{ data: { id: number } }>(`/api/knowledge/entries/${id}/approve`).then((r) => r.data.data),

  reject: (id: number, note: string) => api.post<{ data: { id: number } }>(`/api/knowledge/entries/${id}/reject`, { note }).then((r) => r.data.data),

  update: (id: number, body: Partial<{ text: string; agentId: string; status: 'active' | 'paused' | 'retired' } & Scope>) =>
    saving(api.patch<{ data: { id: number } }>(`/api/knowledge/entries/${id}`, body).then((r) => r.data.data)),

  propose: (messageId: number) =>
    api.post<{ data: Proposal }>(`/api/knowledge/answers/${messageId}/proposal`).then((r) => r.data.data),

  saveFromAnswer: (messageId: number, body: { agentId?: string; facts: ProposedFact[] }) =>
    saving(api.post<{ data: { ids: number[]; pending: boolean } }>(`/api/knowledge/answers/${messageId}/entries`, body).then((r) => r.data.data)),

  report: (messageId: number, body: { whatWrong: string; whatRight?: string; pauseUsed: boolean }) =>
    api.post<{ data: { id: number; paused: number } }>(`/api/knowledge/answers/${messageId}/reports`, body).then((r) => r.data.data),

  usedBy: (messageId: number) =>
    api.get<{ data: { used: UsedKnowledge[] } }>(`/api/knowledge/answers/${messageId}/used`).then((r) => r.data.data.used),

  reports: (status = 'all') =>
    api.get<{ data: { reports: KnowledgeReport[] } }>('/api/knowledge/reports', { params: { status } }).then((r) => r.data.data.reports),

  dismiss: (id: number, note: string) =>
    api.patch<{ data: { id: number; resumed: number } }>(`/api/knowledge/reports/${id}`, { status: 'dismissed', note }).then((r) => r.data.data),
}

export const knowledgeKeys = {
  entries: (f: EntryFilters) => ['knowledge', 'entries', f] as const,
  options: ['knowledge', 'options'] as const,
  reports: (status: string) => ['knowledge', 'reports', status] as const,
  all: ['knowledge'] as const,
}
